import { Spin } from "antd";
import { Icon, LatLngBounds } from "leaflet";
import "leaflet/dist/leaflet.css";
import React, { useEffect, useRef, useState } from "react";
import {
  MapContainer,
  Marker,
  Polygon,
  Polyline,
  Popup,
  TileLayer,
  useMapEvent,
} from "react-leaflet";
import { LinearRing, MarkerColor } from "../../api";
import { useDimension } from "../../util";

const createMarkerIcon = (color: string) =>
  new Icon({
    iconUrl: `${process.env.PUBLIC_URL}/images/marker-icon-2x-${color}.png`,
    shadowUrl: `${process.env.PUBLIC_URL}/images/marker-shadow.png`,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });

const icons: Record<string, Icon> = {
  blue: createMarkerIcon("blue"),
  green: createMarkerIcon("green"),
  gray: createMarkerIcon("gray"),
  red: createMarkerIcon("red"),
  orange: createMarkerIcon("orange"),
};

export interface Position {
  lat: number;
  lon: number;
}

export interface MarkerData extends Position {
  title: string;
  id?: string;
  color?: MarkerColor;
  hasError?: boolean;
  errorText?: React.ReactNode;
  errorSeverity?: "error" | "warning";
  popUpOnLoad?: boolean;
  onClick?: () => void;
}

export interface PolyLineData {
  line: Position[];
  id?: string;
  title?: string;
  color?: React.CSSProperties["color"];
  width?: number;
  onClick?: () => void;
}

export interface PolygonData {
  title?: string;
  ring: LinearRing;
  color?: React.CSSProperties["color"];
  id?: string;
  onClick?: () => void;
}

interface MapProps {
  markers: MarkerData[];
  isLoading?: boolean;
  polylines?: PolyLineData[];
  polygons?: PolygonData[];
  testId?: string;
  updateBounds?: boolean;
  onMapClicked?: (e: L.LeafletMouseEvent) => void;
  onMapBoundsChanged?: (changed: boolean) => void;
}

interface ClickOutsideHandlerProps {
  onMapClick: (e: L.LeafletMouseEvent) => void;
}

// This component is used to handle clicks on map outside of markers
const ClickOutsideHandler = ({ onMapClick }: ClickOutsideHandlerProps) => {
  useMapEvent("click", (e) => {
    if (
      !(e.originalEvent.target as HTMLElement).closest(".leaflet-interactive")
    ) {
      onMapClick(e);
    }
  });

  return null;
};

export function Map({
  markers,
  isLoading,
  polylines,
  polygons,
  testId,
  updateBounds,
  onMapClicked,
  onMapBoundsChanged,
}: MapProps) {
  const mapRef = useRef<L.Map | null>(null);
  const containerRef = useRef<HTMLDivElement>(null!);
  const dimension = useDimension(containerRef);
  const setDimensionHandler = dimension.setHandler;
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    if (mapRef.current && !isLoading) {
      setMapLoaded(true);
    }
  }, [mapRef.current, isLoading]);

  useEffect(() => {
    if (!mapRef.current || !markers) {
      return;
    }

    if (updateBounds) {
      if (markers.length === 1) {
        mapRef.current.setView([markers[0].lat, markers[0].lon], 19);
      } else if (markers.length > 1) {
        updateMapBounds(markers, polygons || [], polylines || [], mapRef);
      }

      if (onMapBoundsChanged) onMapBoundsChanged(true);
      return;
    }
    /*
      Defualt to set bounds once the data first avaiable. Once the map is loaded, bounds are set with 
      the updateBounds prop to allow for greater control over when the bounds are set.
    */
    if (markers.length === 1 && !mapLoaded) {
      mapRef.current.setView([markers[0].lat, markers[0].lon], 19);
    } else if (markers.length > 1 && !isLoading && !mapLoaded) {
      updateMapBounds(markers, polygons || [], polylines || [], mapRef);

      if (onMapBoundsChanged) onMapBoundsChanged(true);
    }
  }, [markers, mapRef.current, isLoading, polygons, polylines]);

  const MarkerWithPopupOnLoad: React.FC<MarkerData> = (marker) => {
    const markerRef = useRef<L.Marker>(null);

    useEffect(() => {
      const leafletMarker = markerRef.current;
      if (leafletMarker) {
        leafletMarker.openPopup();
      }
    }, [mapRef.current]);

    return (
      <Marker
        title={marker.title}
        key={marker.title}
        position={[marker.lat, marker.lon]}
        icon={icons[marker.color || "blue"]}
        eventHandlers={{ click: marker.onClick }}
        ref={markerRef}
      >
        <Popup autoClose={false} closeOnClick={false}>
          {marker.title}
        </Popup>
      </Marker>
    );
  };

  return (
    <div
      style={{ height: "100%", width: "100%", position: "relative" }}
      ref={containerRef}
      data-testid={testId ? testId : "map"}
    >
      <MapContainer
        center={[51.378, 10.052]}
        zoom={6}
        maxZoom={23}
        style={{ height: "100%", width: "100%" }}
        whenCreated={(mapInstance) => {
          mapRef.current = mapInstance;
          setDimensionHandler(() => {
            try {
              mapInstance.invalidateSize();
            } catch {}
          });
        }}
      >
        <TileLayer
          maxZoom={23}
          maxNativeZoom={19}
          attribution='&amp;copy <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {onMapClicked && <ClickOutsideHandler onMapClick={onMapClicked} />}
        {markers.map((marker) =>
          marker.popUpOnLoad ? (
            <MarkerWithPopupOnLoad {...marker} key={marker.title} />
          ) : (
            <Marker
              title={marker.title}
              key={marker.title}
              position={[marker.lat, marker.lon]}
              icon={icons[marker.color || "blue"]}
              eventHandlers={{ click: marker.onClick }}
            >
              <Popup>{marker.title}</Popup>
            </Marker>
          )
        )}
        {polygons &&
          polygons.map((polygon, index) => (
            <Polygon
              key={index}
              positions={polygon.ring.map((p) => [p.lat, p.lon])}
              pathOptions={{ color: polygon.color }}
              eventHandlers={{ click: polygon.onClick }}
            />
          ))}
        {polylines &&
          polylines.map((polyline, index) => (
            <Polyline
              key={index}
              color={polyline.color || "#1890ff"}
              weight={polyline.width || 3}
              positions={polyline.line.map((p) => [p.lat, p.lon])}
              eventHandlers={{ click: polyline.onClick }}
            ></Polyline>
          ))}
        <Spin
          className="centered"
          aria-label="loading-spinner"
          spinning={isLoading}
          size="large"
          style={{
            width: "100%",
            height: "100%",
            zIndex: 1000,
            display: isLoading ? "flex" : "none",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(255,255,255,0.3)",
          }}
        />
      </MapContainer>
    </div>
  );
}

const updateMapBounds = (
  markers: MarkerData[],
  polygons: PolygonData[],
  polylines: PolyLineData[],
  mapRef: React.MutableRefObject<L.Map | null>
) => {
  if (!mapRef.current) return;

  const bounds = new LatLngBounds([]);

  markers.forEach((marker) => {
    bounds.extend([marker.lat, marker.lon]);
  });

  polygons?.forEach((polygon) => {
    polygon.ring.forEach((point) => {
      bounds.extend([point.lat, point.lon]);
    });
  });

  polylines?.forEach((polyline) => {
    polyline.line.forEach((point) => {
      bounds.extend([point.lat, point.lon]);
    });
  });

  mapRef.current.fitBounds(bounds, { maxZoom: 19 });
};
